import { useEffect, useState } from 'react';
import { AlertCircle, Loader2, RefreshCw } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { aiPreviewService, type PreviewScans, type PushPreview, type PushRow } from '@/services/aiPreview';

const CHANGE_BADGES: Record<PushRow['change'], { label: string; tone: 'success' | 'warning' | 'neutral' | 'danger' }> = {
  new: { label: 'New', tone: 'success' },
  changed: { label: 'Changed', tone: 'warning' },
  same: { label: 'Same', tone: 'neutral' },
  cleared: { label: 'Cleared', tone: 'danger' },
};

const TAG_STATES: Record<PushPreview['tags'][number]['state'], { label: string; tone: 'success' | 'neutral' }> = {
  new: { label: 'New', tone: 'success' },
  on_contact: { label: 'Already on the contact', tone: 'neutral' },
  sent_before: { label: 'Sent before, not sent again', tone: 'neutral' },
};

const GHL_MESSAGES: Record<PushPreview['ghl'], string> = {
  found: 'Contact found in the marketing GHL. Below is what the next sync would change on it.',
  missing: "This account isn't in the marketing GHL yet. The next sync would create its contact with these values.",
  excluded: 'The sync never writes this account (it opted out, has no owner email, or is our own backoffice), so nothing below would happen.',
  off: 'GHL marketing is off on this server, so the sync writes nothing.',
  unreachable: "GHL couldn't return the contact right now, so its current values are missing.",
};

export function PushTab({
  publicId, scans, scansKey, canRun,
}: { publicId: string; scans: PreviewScans; scansKey: string; canRun: boolean }) {
  const [push, setPush] = useState<PushPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    // The API refuses internal viewers, and the API client logs out on a refusal.
    if (!canRun) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    aiPreviewService
      .push(publicId, scans)
      .then((p) => !cancelled && setPush(p))
      .catch((err) => !cancelled && setError(err.response?.data?.message || "Couldn't work out what would be pushed"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // scansKey stands for scans, which is a new object on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicId, scansKey, reload, canRun]);

  if (!canRun) {
    return <Card className="py-4 px-5 text-sm text-muted-foreground">Internal viewers can't run previews.</Card>;
  }

  if (!push) {
    return loading ? (
      <div role="status" className="flex justify-center py-16">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
        <span className="sr-only">Loading</span>
      </div>
    ) : (
      <Card className="py-4 px-5 flex-row items-center gap-3 text-sm">
        <AlertCircle className="size-4 text-destructive" />
        {error}
      </Card>
    );
  }

  const counts = { new: 0, changed: 0, same: 0, cleared: 0 };
  for (const f of push.fields) counts[f.change]++;

  return (
    <div className={cn('flex flex-col gap-5', loading && 'opacity-60')}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <span className="font-semibold">
            {push.contact ? [push.contact.firstName, push.contact.email].filter(Boolean).join(' · ') : 'No contact to compare with'}
          </span>
          <span className="text-[13px] text-muted-foreground">{GHL_MESSAGES[push.ghl]}</span>
        </div>
        <div className="flex items-center gap-2">
          <Badge tone="info">Nothing below is written to GHL</Badge>
          <Button variant="outline" size="sm" disabled={loading} onClick={() => setReload((n) => n + 1)}>
            {loading ? <Loader2 className="animate-spin" /> : <RefreshCw />}
            Refresh
          </Button>
        </div>
      </div>

      <Card className="gap-0 py-0">
        <div className="flex flex-wrap items-baseline justify-between gap-2 px-5 py-4 border-b">
          <h2 className="text-base font-semibold">Custom fields</h2>
          <span className="text-[13px] text-muted-foreground">
            {counts.new} new · {counts.changed} changed · {counts.same} unchanged{counts.cleared ? ` · ${counts.cleared} cleared` : ''}
          </span>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-5">Field</TableHead>
                <TableHead>In GHL now</TableHead>
                <TableHead>Would be</TableHead>
                <TableHead className="pr-5">Change</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {push.fields.map((f) => (
                <TableRow key={f.name}>
                  <TableCell className="pl-5 font-mono text-xs align-top whitespace-nowrap">{f.name}</TableCell>
                  <TableCell className="align-top max-w-[280px]">
                    <FieldValue value={f.current} />
                  </TableCell>
                  <TableCell className="align-top max-w-[360px]">
                    <FieldValue value={f.next} />
                  </TableCell>
                  <TableCell className="pr-5 align-top">
                    <Badge tone={CHANGE_BADGES[f.change].tone}>{CHANGE_BADGES[f.change].label}</Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <p className="px-5 py-3 border-t text-xs text-muted-foreground">
          Only the fields the AI emails read. The sync sends its other fields (plan, billing, signup details) as it does today.
        </p>
      </Card>

      <div className="grid gap-5 lg:grid-cols-2 items-start">
        <Card className="gap-0 py-0">
          <div className="flex items-baseline justify-between gap-2 px-5 py-4 border-b">
            <h2 className="text-base font-semibold">Notes that would be added</h2>
            <span className="text-[13px] text-muted-foreground">One per scanned page</span>
          </div>
          <div className="px-5 py-4 flex flex-col gap-3">
            {push.notes.length === 0 && <p className="text-sm text-muted-foreground">No scans used, so no notes.</p>}
            {push.notes.map((note, i) => (
              <div key={i} className="flex flex-col gap-1.5">
                {note.state === 'added_before' && (
                  <Badge tone="neutral">Already on the contact, not added again</Badge>
                )}
                <pre
                  className={cn(
                    'whitespace-pre-wrap break-words rounded-md bg-muted/50 px-3 py-2.5 font-mono text-xs leading-relaxed',
                    note.state === 'added_before' && 'opacity-60',
                  )}
                >
                  {note.body}
                </pre>
              </div>
            ))}
          </div>
        </Card>

        <Card className="gap-0 py-0">
          <div className="px-5 py-4 border-b">
            <h2 className="text-base font-semibold">Tags that would be added</h2>
          </div>
          <div className="px-5 py-4 flex flex-col gap-3">
            {push.tags.length === 0 && <p className="text-sm text-muted-foreground">No scans used, so no tags.</p>}
            {push.tags.map((t) => (
              <div key={t.tag} className="flex items-center justify-between gap-3">
                <span className="font-mono text-[13px]">{t.tag}</span>
                <Badge tone={TAG_STATES[t.state].tone}>{TAG_STATES[t.state].label}</Badge>
              </div>
            ))}
            <p className="text-xs text-muted-foreground leading-relaxed">
              Tags already on the contact stay. Each context tag is added once, so a GHL workflow that removes it isn't fired again.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}

function FieldValue({ value }: { value: string | null }) {
  const [open, setOpen] = useState(false);
  if (value === null) return <span className="text-muted-foreground italic">unknown</span>;
  if (value === '') return <span className="text-muted-foreground italic">empty</span>;
  const long = value.length > 140;
  return (
    <div className="text-[13px] whitespace-pre-wrap break-words">
      {long && !open ? `${value.slice(0, 140)}...` : value}
      {long && (
        <button type="button" onClick={() => setOpen(!open)} className="ml-1 text-xs text-blue-700 hover:underline dark:text-blue-400">
          {open ? 'Show less' : 'Show all'}
        </button>
      )}
    </div>
  );
}
